import { createTheme } from "@mui/material/styles";

/**
 * AOS Pathways Brand Theme
 *
 * Based on AOS Brand Guide:
 * - Primary font: Aileron
 * - Primary color: PMS 185 C (#E31837)
 * - Secondary color: Dark Navy (#1A2B4A)
 */

const aosTheme = createTheme({
  // Custom breakpoints for responsive design
  // xs: 0-599px (mobile), sm: 600-767px (large mobile), md: 768-1023px (tablet)
  // lg: 1024-1279px (desktop), xl: 1280px+ (large desktop)
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 768,
      lg: 1024,
      xl: 1280,
    },
  },
  palette: {
    mode: "light",
    primary: {
      main: "#E31837",
      light: "#FF4D5E",
      dark: "#B30000",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#1A2B4A",
      light: "#2C4066",
      dark: "#0D1A30",
      contrastText: "#FFFFFF",
    },
    background: {
      default: "#F5F5F5",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#1A2B4A",
      secondary: "#666666",
    },
    success: {
      main: "#4CAF50",
    },
    warning: {
      main: "#FF9800",
    },
    error: {
      main: "#F44336",
    },
    info: {
      main: "#2196F3",
    },
  },
  typography: {
    fontFamily: '"Aileron", "Bierstadt", "Aptos", sans-serif',
    h1: {
      fontFamily: '"Aileron", sans-serif',
      fontWeight: 700,
      fontSize: "2.5rem",
      lineHeight: 1.2,
    },
    h2: {
      fontFamily: '"Aileron", sans-serif',
      fontWeight: 600,
      fontSize: "2rem",
      lineHeight: 1.3,
    },
    h3: {
      fontFamily: '"Aileron", sans-serif',
      fontWeight: 600,
      fontSize: "1.5rem",
      lineHeight: 1.4,
    },
    h4: {
      fontFamily: '"Aileron", sans-serif',
      fontWeight: 600,
      fontSize: "1.25rem",
      lineHeight: 1.4,
    },
    h5: {
      fontFamily: '"Aileron", sans-serif',
      fontWeight: 600,
      fontSize: "1.1rem",
      lineHeight: 1.4,
    },
    h6: {
      fontFamily: '"Aileron", sans-serif',
      fontWeight: 600,
      fontSize: "1rem",
      lineHeight: 1.4,
    },
    body1: {
      fontFamily: '"Aileron", sans-serif',
      fontSize: "1rem",
      lineHeight: 1.6,
    },
    body2: {
      fontFamily: '"Aileron", sans-serif',
      fontSize: "0.875rem",
      lineHeight: 1.5,
    },
    button: {
      fontFamily: '"Aileron", sans-serif',
      fontWeight: 600,
      textTransform: "none",
    },
    caption: {
      fontFamily: '"Aileron", sans-serif',
      fontSize: "0.75rem",
    },
    overline: {
      fontFamily: '"Aileron", sans-serif',
      fontSize: "0.75rem",
      textTransform: "uppercase",
      letterSpacing: "0.08em",
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    // Global focus-visible styles for accessibility (WCAG 2.4.7)
    MuiCssBaseline: {
      styleOverrides: {
        // Ensure proper viewport height for footer positioning
        html: {
          height: "100%",
        },
        body: {
          height: "100%",
          margin: 0,
        },
        "#root": {
          height: "100%",
        },
        // Focus visible styles for all focusable elements
        "*:focus-visible": {
          outline: "2px solid #1A2B4A",
          outlineOffset: "2px",
        },
        // Remove focus outline for mouse users
        "*:focus:not(:focus-visible)": {
          outline: "none",
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          "&:focus-visible": {
            outline: "2px solid #1A2B4A",
            outlineOffset: "2px",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: "8px 24px",
          fontWeight: 600,
        },
        containedPrimary: {
          "&:hover": {
            backgroundColor: "#B30000",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#FFFFFF",
          color: "#1A2B4A",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
          },
        },
      },
    },
    // Touch target sizing for mobile accessibility (WCAG 2.5.5)
    MuiIconButton: {
      styleOverrides: {
        root: {
          minWidth: 44,
          minHeight: 44,
        },
        sizeSmall: {
          minWidth: 44,
          minHeight: 44,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          minHeight: 48,
        },
      },
    },
  },
});

export default aosTheme;
